import { supabase } from '@/lib/supabase';
import { separarDataHora } from '@/pages/assistente-evento/utils';
import type { FormaParticipar, MembroEquipe } from '@/pages/assistente-evento/types';

export interface DadosEdicao {
  evento: Record<string, unknown>;
  participacoes: FormaParticipar[];
  equipe: MembroEquipe[];
  inscritosTotal: number;
  taxas: { loja: string | null; pix: string | null; cartao: string | null; parcelado: string | null };
}

interface ModalidadeDB {
  id: number;
  nome: string;
  descricao: string | null;
  publica: boolean;
  inscricoes_abrem_em: string | null;
  inscricoes_fecham_em: string | null;
  vagas_total: number | null;
  valor_centavos: number;
  valor_definido: boolean;
  ordem: number;
  exige_identificacao: boolean;
  max_por_compra: number;
  validacao_presencial: boolean;
}

// Carrega tudo que o painel de editar precisa para um evento.
export async function carregarEdicao(eventoId: number, userId: string): Promise<DadosEdicao> {
  const { data: ev, error: errEv } = await supabase
    .from('eventos')
    .select('*')
    .eq('id', eventoId)
    .maybeSingle();
  if (errEv) throw errEv;
  if (!ev) throw new Error('Evento não encontrado.');

  const evData = ev as Record<string, unknown>;
  const ehDono = evData.dono_id === userId;

  const [resMods, resPainel] = await Promise.all([
    supabase
      .from('modalidades')
      .select(
        'id, nome, descricao, publica, inscricoes_abrem_em, inscricoes_fecham_em, vagas_total, valor_centavos, valor_definido, ordem, exige_identificacao, max_por_compra, validacao_presencial',
      )
      .eq('evento_id', eventoId)
      .order('ordem'),
    supabase.from('vw_painel_evento').select('modalidade_id, ativas').eq('evento_id', eventoId),
  ]);
  if (resMods.error) throw resMods.error;
  if (resPainel.error) throw resPainel.error;

  const mods = (resMods.data as ModalidadeDB[]) || [];
  const inscritosPorModalidade = new Map<number, number>();
  ((resPainel.data as { modalidade_id: number; ativas: number }[]) || []).forEach((r) => {
    inscritosPorModalidade.set(r.modalidade_id, Number(r.ativas) || 0);
  });

  const idsMods = mods.map((m) => m.id);
  let opcoes: Record<string, unknown>[] = [];
  let formularios: Record<string, unknown>[] = [];
  if (idsMods.length > 0) {
    const [resOp, resForm] = await Promise.all([
      supabase
        .from('opcoes')
        .select('id, modalidade_id, nome, vagas, valor_centavos, ordem')
        .in('modalidade_id', idsMods)
        .order('ordem'),
      supabase.from('formularios').select('id, modalidade_id, nome').in('modalidade_id', idsMods),
    ]);
    if (resOp.error) throw resOp.error;
    if (resForm.error) throw resForm.error;
    opcoes = (resOp.data as Record<string, unknown>[]) || [];
    formularios = (resForm.data as Record<string, unknown>[]) || [];
  }

  const codigos = new Map<number, string>();
  await Promise.all(
    mods.map(async (m) => {
      const { data } = await supabase.rpc('codigo_da_modalidade', { p_modalidade_id: m.id });
      if (typeof data === 'string') codigos.set(m.id, data);
    }),
  );

  const formPorModalidade = new Map<number, { id: number; nome: string }>();
  formularios.forEach((f) => {
    formPorModalidade.set(f.modalidade_id as number, {
      id: f.id as number,
      nome: f.nome as string,
    });
  });

  const lista: FormaParticipar[] = mods.map((m) => {
    const grupos = opcoes
      .filter((o) => o.modalidade_id === m.id)
      .map((o) => ({
        id: o.id as number,
        nome: o.nome as string,
        vagas: o.vagas as number | null,
        valorCentavos: o.valor_centavos as number,
      }));
    const form = formPorModalidade.get(m.id);
    const aberturas = separarDataHora(m.inscricoes_abrem_em || '');
    const fechamentos = separarDataHora(m.inscricoes_fecham_em || '');
    const valorModo: FormaParticipar['valorModo'] =
      m.valor_definido === false
        ? 'a_definir'
        : grupos.length > 0
          ? grupos.every((g) => g.valorCentavos === 0)
            ? 'gratuito'
            : 'pago'
          : m.valor_centavos === 0
            ? 'gratuito'
            : 'pago';
    return {
      id: m.id,
      nome: m.nome,
      descricao: m.descricao || '',
      publica: m.publica,
      codigoAcesso: codigos.get(m.id) || '',
      inscricoesAbremData: aberturas.data,
      inscricoesAbremHora: aberturas.hora,
      inscricoesFechamData: fechamentos.data,
      inscricoesFechamHora: fechamentos.hora,
      temLimite: m.vagas_total !== null && m.vagas_total !== undefined,
      vagas: m.vagas_total,
      dividirGrupos: grupos.length > 0,
      valorCentavos: m.valor_centavos,
      valorModo,
      grupos,
      temFormulario: !!form,
      formularioId: form ? form.id : null,
      formularioNome: form ? form.nome : '',
      inscritos: inscritosPorModalidade.get(m.id) ?? 0,
      exigeIdentificacao: m.exige_identificacao,
      maxPorCompra: m.max_por_compra,
      validacaoPresencial: m.validacao_presencial === true,
    };
  });

  let totalInscritos = 0;
  inscritosPorModalidade.forEach((n) => {
    totalInscritos += n;
  });

  let equipeLista: MembroEquipe[] = [];
  if (ehDono) {
    const { data: resEq } = await supabase
      .from('vw_equipe_evento')
      .select('id, email, status, o_que_significa')
      .eq('evento_id', eventoId)
      .order('criado_em');
    equipeLista = (
      (resEq as { id: number; email: string | null; status: string; o_que_significa: string | null }[]) ||
      []
    )
      .filter((e) => e.status !== 'removido')
      .map((e) => ({
        id: e.id,
        email: e.email || '',
        status: e.status,
        oQueSignifica: e.o_que_significa || '',
      }));
  }

  return {
    evento: evData,
    participacoes: lista,
    equipe: equipeLista,
    inscritosTotal: totalInscritos,
    taxas: {
      loja: (evData.loja_quem_paga_taxa as string) ?? null,
      pix: (evData.quem_paga_taxa as string) ?? null,
      cartao: (evData.quem_paga_taxa_cartao as string) ?? null,
      parcelado: (evData.quem_paga_taxa_parcelado as string) ?? null,
    },
  };
}